import { ControlReassignment } from "api/generated/model";
import { TagsQuerySelect } from "org/tags/queries/useTagsQuery";

import { AssignmentRow } from "./spreadsheetParser";

export function convertToControlReassignments(
    data: AssignmentRow[],
    tagsData: TagsQuerySelect
): Record<string, ControlReassignment[]> {
    const emptyReassignments: Record<string, ControlReassignment[]> = {};
    return data.reduce((acc, row) => {
        if (!acc[row.Control_ID]) {
            acc[row.Control_ID] = [];
        }

        let tagIds: string[] = [];
        if (row.tagColumns) {
            tagIds = Object.entries(row.tagColumns).flatMap(
                ([category, tagNames]) => {
                    const categoryData = tagsData.tagCategories.find(
                        (c) =>
                            c.name.toLowerCase() === category.toLowerCase() &&
                            c.active
                    );
                    if (!categoryData) {
                        throw new Error(
                            `Category not found or inactive: ${category}`
                        );
                    }

                    return tagNames.flatMap((tagName: string) => {
                        const tag = tagsData.tags.find(
                            (t) =>
                                t.tagCategoryId === categoryData.id &&
                                t.name.toLowerCase() ===
                                    tagName.toLowerCase() &&
                                t.active
                        );
                        if (!tag) {
                            throw new Error(
                                `Tag not found or inactive: ${tagName} in category ${category}`
                            );
                        }
                        return tag.id;
                    });
                }
            );
        }

        acc[row.Control_ID].push({
            initialAssignment: (() => {
                if (row.GroupAssignment_Type === "Group") {
                    return {
                        type: "Pool",
                        userGroupId: row.Responsible_Group_ID!,
                        defaultClaimantId: row.Default_Claimant_ID ?? null,
                    };
                }
                if (row.GroupAssignment_Type === "Individual") {
                    return {
                        type: "Group",
                        userGroupId: row.Responsible_Group_ID!,
                    };
                }
                // If not Pool or Group, it must be User because we have already validated the input
                return {
                    type: "User",
                    userId: row.Responsible_ID!,
                };
            })(),
            subsequentAssignments: [
                (() => {
                    if (row.Reviewer_ID) {
                        return {
                            type: "User",
                            userId: row.Reviewer_ID,
                        };
                    }
                    if (row.Reviewer_Group_ID) {
                        return {
                            type: "Pool",
                            userGroupId: row.Reviewer_Group_ID,
                        };
                    }
                    return null;
                })(),
                (() => {
                    if (row.SecondReviewer_ID) {
                        return {
                            type: "User",
                            userId: row.SecondReviewer_ID,
                        };
                    }
                    if (row.SecondReviewer_Group_ID) {
                        return {
                            type: "Pool",
                            userGroupId: row.SecondReviewer_Group_ID,
                        };
                    }
                    return null;
                })(),
            ],
            tagIds,
            entityId: row.Entity_ID ?? null,
        });
        return acc;
    }, emptyReassignments);
}
